import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { MembroQuadroDto } from '@nexital/shared';
import { Avatar } from '../design-system/Avatar';
import { Dialogo } from '../design-system/Dialogo';

type Props = {
  /** Pessoas do quadro (sem o autor). */
  pessoas: MembroQuadroDto[];
  selecionadas: string[];
  /** Post-it ligado a um cartão: só quem vê o cartão pode ser marcado (RQ-NOT-008). */
  exigeCartao: boolean;
  aoConfirmar: (usuarioUuids: string[]) => void;
  aoCancelar: () => void;
};

const semAcesso = (p: MembroQuadroDto) => `${p.nome.split(' ')[0]} não tem permissão para ver o cartão`;

/**
 * Escolhe com quem o post-it é compartilhado (RQ-NOT-001, RQ-NOT-007); sem ninguém marcado, o compartilhamento é
 * encerrado. Vai para o body: dentro do nó, o clique nas caixas disputaria com o arraste do quadro.
 */
export function DialogoCompartilhar({ pessoas, selecionadas, exigeCartao, aoConfirmar, aoCancelar }: Props) {
  const [marcadas, setMarcadas] = useState(() => new Set(selecionadas));
  const alternar = (uuid: string) =>
    setMarcadas((atual) => {
      const nova = new Set(atual);
      if (nova.has(uuid)) nova.delete(uuid);
      else nova.add(uuid);
      return nova;
    });
  return createPortal(
    <Dialogo
      titulo="Compartilhar post-it"
      descricao="Só as pessoas marcadas (e você) verão este post-it. Desmarque todas para encerrar o compartilhamento."
      rotuloConfirmar="Salvar"
      aoConfirmar={() => aoConfirmar([...marcadas])}
      aoCancelar={aoCancelar}
    >
      <ul className="compartilhar__lista">
        {pessoas.map((p) => {
          // Quem já estava marcado pode ser desmarcado mesmo sem acesso ao cartão.
          const bloqueada = exigeCartao && !p.veCartoes && !marcadas.has(p.uuid);
          return (
            <li key={p.uuid} title={exigeCartao && !p.veCartoes ? semAcesso(p) : undefined}>
              <label className={`compartilhar__pessoa${bloqueada ? ' compartilhar__pessoa--bloqueada' : ''}`}>
                <input type="checkbox" checked={marcadas.has(p.uuid)} disabled={bloqueada} onChange={() => alternar(p.uuid)} />
                <Avatar nome={p.nome} chave={p.uuid} tamanho={22} />
                {p.nome}
              </label>
            </li>
          );
        })}
      </ul>
    </Dialogo>,
    document.body,
  );
}
